import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
  RefModelStoreService,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {BehaviorSubject} from 'rxjs';

import {CustomViewDaoService} from '../../../../../../core/services/custom-view-dao.service';
import {ICustomView} from '../../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../../services/custom-view.service';
import {ISelectedMetadata} from '../../models/create-new-view.interface';

@Component({
  selector: 'app-edit-custom-view-columns-dialog',
  styleUrls: ['./edit-custom-view-columns-dialog.component.scss'],
  templateUrl: './edit-custom-view-columns-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCustomViewColumnsDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  public title = 'RUBIC_CUBE.EDIT_VIEW_DIALOG_TITLE';
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public customView: ICustomView;

  metadataList: string[];
  allNodes: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditCustomViewColumnsDialogComponent>,
    private customViewService: CustomViewService,
    private translate: TranslateService,
    private changeRef: ChangeDetectorRef,
    private customViewDaoService: CustomViewDaoService,
    private rmeStore: RefModelStoreService,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.data.view) {
      this.fetchCustomView();
    } else {
      this.handleCustomView(this.data.view);
    }
  }

  private fetchCustomView() {
    const refModel = this.rmeStore.refModel;
    this.customViewDaoService.getCustomView(refModel._id, this.data.viewId).subscribe(
      customView => this.handleCustomView(customView),
      error => {
        this.snackBarService.error(error.error.error);
        this.closeModal();
      }
    );
  }

  private handleCustomView(view: ICustomView) {
    this.customView = view;
    this.customViewService.setSelectedColumnFromCustomView(this.customView);

    this.metadataList = this.customView.metadataList;
    this.allNodes = this.customView.allNodes;

    this.customViewService.storeSelectedMetadata({
      selectedItems: this.metadataList,
      allNodes: this.allNodes
    } as ISelectedMetadata);

    this.fetchAvailableColumnsList();
  }

  public closeModal() {
    this.dialogRef.close();
  }

  private fetchAvailableColumnsList() {
    this.customViewDaoService
      .getAvailableColumnsList(this.customView.refModel, {
        ...(!this.allNodes && {metadataList: this.metadataList || []}),
        allNodes: this.allNodes
      })
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(availableColumns => {
        this.customViewService.storeRefModelColumns(availableColumns);
        this.customViewService.setSelectedColumnFromCustomView(this.customView);
        this.loading$.next(false);
        this.changeRef.detectChanges();
      });
  }

  public updateCustomView($event) {
    this.loading$.next(true);

    const {selectedColumns} = $event;
    const {refModel, _id, name, dynamicalView} = this.customView;

    if (dynamicalView) {
      this.loading$.next(false);
      this.dialogRef.close({columns: selectedColumns.map(column => column.field)});

      return;
    }

    const payload = {
      columns: selectedColumns.map(column => column.field.value)
    };

    this.customViewDaoService
      .updateCustomView(refModel, _id, payload)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(
        response => {
          this.customViewService.updateCustomView(response);
          const msg = this.translate.instant('RUBIC_CUBE.EDITED_SUCCESSFULLY', {name});
          this.loading$.next(false);
          this.dialogRef.close({data: response, msg});
        },
        errorResponse => {
          const msg = this.translate.instant(
            errorResponse.error.error || 'RUBIC_CUBE.EDITED_FAILURE',
            {name}
          );
          this.loading$.next(false);
          this.dialogRef.close({msg});
        }
      );
  }

  public close() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.customViewService.resetModal();
  }
}
